import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { GeometricBackground } from '../components/GeometricBackground';
import { KhatimStarLogo } from '../components/AalimLogo';
import { Button } from '../components/ui/button';
import { ArrowLeft, Calendar, Eye, Tag, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch(`${API_URL}/api/blog/posts/${slug}`)
            .then(r => {
                if (!r.ok) throw new Error('Post not found');
                return r.json();
            })
            .then(data => { setPost(data); setLoading(false); })
            .catch(e => { setError(e.message); setLoading(false); });
    }, [slug]);

    // Set SEO meta tags
    useEffect(() => {
        if (post) {
            document.title = `${post.meta_title || post.title} | aalim.ai Blog`;
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) metaDesc.content = post.meta_description || post.excerpt;
            
            // Open Graph tags
            const setMeta = (property, content) => {
                let tag = document.querySelector(`meta[property="${property}"]`);
                if (!tag) {
                    tag = document.createElement('meta');
                    tag.setAttribute('property', property);
                    document.head.appendChild(tag);
                }
                tag.content = content;
            };
            setMeta('og:title', post.meta_title || post.title);
            setMeta('og:description', post.meta_description || post.excerpt);
            setMeta('og:type', 'article');
            setMeta('og:url', window.location.href);
            if (post.featured_image) setMeta('og:image', post.featured_image);
        }
        return () => { document.title = 'aalim.ai'; };
    }, [post]);

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4" data-testid="blog-not-found">
                <h1 className="text-2xl font-heading font-bold text-foreground">Post Not Found</h1>
                <p className="text-muted-foreground">The blog post you're looking for doesn't exist.</p>
                <Link to="/blogs"><Button variant="outline" className="gap-2 border-secondary/30 hover:border-secondary hover:text-secondary"><ArrowLeft className="w-4 h-4" /> Back to Blog</Button></Link>
            </div>
        );
    }

    // Render content - supports basic markdown-like formatting
    const renderContent = (content) => {
        // Split by double newlines for paragraphs
        const blocks = content.split(/\n\n+/);
        return blocks.map((block, i) => {
            const trimmed = block.trim();
            if (!trimmed) return null;

            // Headers
            if (trimmed.startsWith('### ')) return <h3 key={i} className="text-lg font-heading font-semibold text-foreground mt-8 mb-3">{trimmed.slice(4)}</h3>;
            if (trimmed.startsWith('## ')) return <h2 key={i} className="text-xl font-heading font-semibold text-foreground mt-10 mb-4">{trimmed.slice(3)}</h2>;
            if (trimmed.startsWith('# ')) return <h1 key={i} className="text-2xl font-heading font-bold text-foreground mt-10 mb-4">{trimmed.slice(2)}</h1>;

            // Blockquote (Arabic text or Quran/Hadith)
            if (trimmed.startsWith('> ')) {
                return (
                    <blockquote key={i} className="border-l-4 border-secondary/40 pl-4 py-2 my-6 bg-secondary/5 rounded-r-lg">
                        <p className="text-foreground italic leading-relaxed">{trimmed.slice(2)}</p>
                    </blockquote>
                );
            }

            // Regular paragraph
            return (
                <p key={i} className="text-muted-foreground leading-relaxed mb-4">
                    {trimmed.split(/(\*\*[^*]+\*\*)/).map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={j} className="text-foreground">{part.slice(2, -2)}</strong>;
                        }
                        return part;
                    })}
                </p>
            );
        });
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            <GeometricBackground />

            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-border">
                <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-1">
                        <KhatimStarLogo size={36} />
                        <span className="font-heading text-xl font-semibold text-foreground -ml-1">aalim<span className="text-secondary">.ai</span></span>
                    </Link>
                    <Link to="/blogs">
                        <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="w-4 h-4" /> All Posts</Button>
                    </Link>
                </div>
            </header>

            {/* Article */}
            <article className="pt-28 pb-20 px-6" data-testid="blog-article">
                <div className="max-w-3xl mx-auto">
                    {/* Meta */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                        <div className="flex items-center gap-3 text-sm text-muted-foreground mb-4 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {post.author}</span>
                            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {post.views} views</span>
                        </div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight" data-testid="blog-title">
                            {post.title}
                        </h1>
                        {post.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {post.tags.map(tag => (
                                    <span key={tag} className="text-xs text-secondary bg-secondary/10 px-2.5 py-1 rounded-full flex items-center gap-1 border border-secondary/20">
                                        <Tag className="w-3 h-3" />{tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </motion.div>

                    {/* Featured Image */}
                    {post.featured_image && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.1 } }}
                            className="mb-10 rounded-xl overflow-hidden border border-border">
                            <img src={post.featured_image} alt={post.title} className="w-full" />
                        </motion.div>
                    )}

                    {/* Content */}
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}
                        className="prose-container">
                        {post.content_type === 'html' || post.content?.trim().startsWith('<') ? (
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }} className="prose prose-sm dark:prose-invert max-w-none" />
                        ) : renderContent(post.content)}
                    </motion.div>

                    {/* Back */}
                    <div className="mt-12 pt-8 border-t border-secondary/20">
                        <Link to="/blogs">
                            <Button variant="outline" className="gap-2 border-secondary/30 hover:border-secondary hover:text-secondary"><ArrowLeft className="w-4 h-4" /> Back to Blog</Button>
                        </Link>
                    </div>
                </div>
            </article>

            {/* Footer */}
            <footer className="py-8 border-t border-secondary/20">
                <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <Link to="/about" className="hover:text-foreground transition-colors">About</Link>
                        <Link to="/blogs" className="hover:text-foreground transition-colors">Blog</Link>
                        <Link to="/terms" className="hover:text-foreground transition-colors">Terms & Privacy</Link>
                    </div>
                    <p className="text-sm text-muted-foreground">&copy; 2026 aalim.ai</p>
                </div>
            </footer>
        </div>
    );
}
